import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb } from 'lucide-react';
import type { DashboardMetrics, ArgentinaMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
interface TrafficLightProps {
  metrics?: DashboardMetrics;
  arMetrics?: ArgentinaMetrics;
  country: CountryCode;
}
export function TrafficLight({
  metrics,
  arMetrics,
  country
}: TrafficLightProps) {
  const isAr = country === 'AR' && arMetrics;
  const activeState = isAr ? arMetrics.trafficLightState : metrics!.trafficLightState;
  const indicators = isAr ? [{
    id: 'green' as const,
    status: 'Verde',
    condition: `CPA ≤ ${formatCurrency(arMetrics.cpaTarget20, country)}`,
    action: 'ESCALAR',
    color: 'bg-cyber-green',
    glow: 'shadow-glow-green',
    text: 'text-cyber-green',
    border: 'border-cyber-green/30',
    activeBg: 'bg-cyber-green/10'
  }, {
    id: 'yellow' as const,
    status: 'Amarillo',
    condition: `CPA ${formatCurrency(arMetrics.cpaTarget20, country)} - ${formatCurrency(arMetrics.cpaTarget10, country)}`,
    action: 'OPTIMIZAR CREATIVOS',
    color: 'bg-yellow-400',
    glow: 'shadow-[0_0_20px_rgba(250,204,21,0.2)]',
    text: 'text-yellow-400',
    border: 'border-yellow-400/30',
    activeBg: 'bg-yellow-400/10'
  }, {
    id: 'red' as const,
    status: 'Rojo',
    condition: `CPA > ${formatCurrency(arMetrics.cpaTarget10, country)}`,
    action: 'APAGAR / REVISAR',
    color: 'bg-danger-red',
    glow: 'shadow-glow-red',
    text: 'text-danger-red',
    border: 'border-danger-red/30',
    activeBg: 'bg-danger-red/10'
  }] : [{
    id: 'green' as const,
    status: 'Verde',
    condition: `CPA < ${formatCurrency(metrics!.cpaIdeal, country)}`,
    action: 'ESCALAR',
    color: 'bg-cyber-green',
    glow: 'shadow-glow-green',
    text: 'text-cyber-green',
    border: 'border-cyber-green/30',
    activeBg: 'bg-cyber-green/10'
  }, {
    id: 'yellow' as const,
    status: 'Amarillo',
    condition: `CPA ${formatCurrency(metrics!.cpaIdeal, country)} - ${formatCurrency(metrics!.cpaBreakEven, country)}`,
    action: 'OPTIMIZAR',
    color: 'bg-yellow-400',
    glow: 'shadow-[0_0_20px_rgba(250,204,21,0.2)]',
    text: 'text-yellow-400',
    border: 'border-yellow-400/30',
    activeBg: 'bg-yellow-400/10'
  }, {
    id: 'red' as const,
    status: 'Rojo',
    condition: `CPA > ${formatCurrency(metrics!.cpaBreakEven, country)}`,
    action: 'APAGAR',
    color: 'bg-danger-red',
    glow: 'shadow-glow-red',
    text: 'text-danger-red',
    border: 'border-danger-red/30',
    activeBg: 'bg-danger-red/10'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.5
  }} className="glass-card p-6 md:p-8" data-id="element-250">
      <div className="flex items-center justify-center gap-3 mb-8" data-id="element-251">
        <Lightbulb className="w-5 h-5 text-white/70" data-id="element-252" />
        <h2 className="text-sm font-bold tracking-widest text-white uppercase" data-id="element-253">
          Semáforo de Decisión
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-254">
        {indicators.map(item => {
        const isActive = item.id === activeState;
        return <motion.div key={item.id} animate={isActive ? {
          scale: [1, 1.02, 1]
        } : {}} transition={isActive ? {
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        } : {}} className={`
                flex flex-row md:flex-col items-center p-4 rounded-xl border transition-all
                ${isActive ? `${item.border} ${item.activeBg} ring-1 ${item.border}` : 'border-white/5 bg-black/40 opacity-40'}
              `} data-id="element-255">
              <div className="relative flex-shrink-0 mb-0 md:mb-4 mr-4 md:mr-0" data-id="element-256">
                {isActive && <div className={`absolute inset-0 rounded-full ${item.color} blur-md opacity-60 animate-pulse`} data-id="element-257"></div>}
                <div className={`relative w-5 h-5 md:w-8 md:h-8 rounded-full ${isActive ? `${item.color} ${item.glow}` : 'bg-white/10'}`} data-id="element-258"></div>
              </div>
              <div className="flex-grow md:text-center" data-id="element-259">
                <div className={`text-xs font-medium mb-1 ${isActive ? 'text-white/60' : 'text-white/30'}`} data-id="element-260">
                  {item.condition}
                </div>
                <div className={`text-sm md:text-base font-black tracking-widest uppercase ${isActive ? item.text : 'text-white/20'}`} data-id="element-261">
                  {item.action}
                </div>
                {isActive && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`mt-2 text-[10px] font-bold uppercase tracking-widest ${item.text}`} data-id="element-262">
                    ● ACTIVO
                  </motion.div>}
              </div>
            </motion.div>;
      })}
      </div>
    </motion.div>;
}